/*!

=========================================================
* Light Bootstrap Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-pro-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { Component } from "react";
import CodeMirror from "react-codemirror";
import "codemirror/mode/jsx/jsx";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { prism } from "react-syntax-highlighter/dist/styles/prism";

const options = {
  mode: "jsx",
  readOnly: true,
};
const fileStructure = `Light Bootstrap Dashboard PRO React
.
.
├── CHANGELOG.md
├── Documentation
│   ├── css
│   │   ├── demo.css
│   │   ├── documentation.css
│   │   └── light-bootstrap-dashboard.css
│   ├── img
│   └── tutorial-components.html
├── ISSUE_TEMPLATE.md
├── README.md
├── gulpfile.js
├── jsconfig.json
├── package.json
├── public
│   ├── index.html
│   └── manifest.json
└── src
    ├── index.js
    ├── routes.js
    ├── assets
    │   ├── css
    │   │   ├── demo.css
    │   │   ├── documentation.css
    │   │   ├── light-bootstrap-dashboard-pro-react.css
    │   │   ├── light-bootstrap-dashboard-pro-react.css.map
    │   │   └── light-bootstrap-dashboard-pro-react.min.css
    │   ├── fonts
    │   │   ├── nucleo-icons.eot
    │   │   ├── nucleo-icons.svg
    │   │   ├── nucleo-icons.ttf
    │   │   ├── nucleo-icons.woff
    │   │   └── nucleo-icons.woff2
    │   ├── img
    │   │   ├── faces
    │   │   └── flags
    │   └── sass
    │       ├── lbd
    │       │   ├── mixins
    │       │   └── plugins
    │       ├── lbdpr
    │       │   ├── plugins
    │       │   └── react-differences.scss
    │       └── light-bootstrap-dashboard-pro-react.scss
    ├── layouts
    │   ├── Admin.js
    │   └── Auth.js
    ├── components
    │   ├── FixedPlugin
    │   │   └── FixedPlugin.js
    │   ├── Footers
    │   │   ├── AdminFooter.js
    │   │   └── AuthFooter.js
    │   ├── Navbars
    │   │   ├── AdminNavbar.js
    │   │   └── AuthNavbar.js
    │   ├── ReactTable
    │   │   └── ReactTable.js
    │   ├── Sidebar
    │   │   └── Sidebar.js
    │   └── TagsInput
    │       └── TagsInput.js
    └── views
        ├── Calendar.js
        ├── Charts.js
        ├── Components
        │   ├── Buttons.js
        │   ├── GridSystem.js
        │   ├── Icons.js
        │   ├── Notifications.js
        │   ├── Panels.js
        │   ├── SweetAlertPage.js
        │   └── Typography.js
        ├── Dashboard.js
        ├── Forms
        │   ├── ExtendedForms.js
        │   ├── RegularForms.js
        │   ├── ValidationForms.js
        │   └── Wizard
        │       ├── Step1.js
        │       ├── Step2.js
        │       ├── Step3.js
        │       └── Wizard.js
        ├── Maps
        │   ├── FullScreenMap.js
        │   ├── GoogleMaps.js
        │   └── VectorMap.js
        ├── Pages
        │   ├── LockScreenPage.js
        │   ├── LoginPage.js
        │   ├── RegisterPage.js
        │   └── UserPage.js
        └── Tables
            ├── ExtendedTables.js
            ├── ReactTables.js
            └── RegularTables.js
`;
class TutorialSection extends Component {
  render() {
    return (
      <div className="tim-container">
        <div className="tim-row">
          <h2 className="text-center">Short Description and Usage</h2>
          <legend />
          <p>
            Light Bootstrap Dashboard PRO React is a beautiful resource built
            over Bootstrap, React and React-Bootstrap to allow you to create
            powerful and beautiful dashboards. We have redesigned all the usual
            components in Bootstrap to make it look flat, minimalist and easy to
            use.
            <br />
            <br />
            Using the dashboard is very simple, but it does require you to
            understand basic JavaScript functions, react and react-bootstrap. To
            get the desired effect you will need to integrate react components
            and JS plugins that take a little bit more effort. Down below we
            list all the files you need to include inside the application to get
            going.
          </p>
        </div>
        <div className="tim-row">
          <h2 className="text-center">License</h2>
          <legend />
          <p>
            {
              // eslint-disable-next-line
            }
            When you purchase an item from{" "}
            <a
              href="https://www.creative-tim.com/?ref=lbdpr-docs-tutorial-page"
              target="_blank"
            >
              Creative Tim
            </a>
            , you are actually purchasing a license to use that item. All our
            free items are under MIT license. All our premium items are covered
            by our Personal and Developer licenses. In order to understand the
            rights and restrictions that come with your purchase, please read
            the details from our{" "}
            <a
              href="https://www.creative-tim.com/license?ref=lbdpr-docs-tutorial-page"
              target="_blank"
            >
              Official License Page
            </a>
            .
          </p>
        </div>
        <div className="tim-row">
          <h2 className="text-center">Getting Started</h2>
          <legend />
          <p>
            The Light Bootstrap Dashboard React is built on top of{" "}
            <a
              href="https://5c507d49471426000887a6a7--react-bootstrap.netlify.com/"
              target="_blank"
            >
              react-bootstrap
            </a>{" "}
            (Bootstrap 3), so you can safely use it on your existing or new
            React-Bootstrap project. No line of code from Bootstrap 3 was
            changed, so you don't have to worry about undesired effects in your
            work.
          </p>
          <p>
            We provide all the necessary CSS resources. So, to immediately
            change or get started with our design, include the{" "}
            <code>src/assets/scss/light-bootstrap-dashboard.css</code> in your
            HTML template. Your project will get the new look.
          </p>
          <h2 id="content" className="text-center">
            Local Development
          </h2>
          <ul>
            <li>
              Install NodeJS LTS version from{" "}
              <a href="https://nodejs.org/en/" target="_blank">
                NodeJS Official Page
              </a>{" "}
              (NOTE: you need the LTS version, as our product only works with LTS version)
            </li>
            <li>
              Go to{" "}
              <a
                href="https://www.creative-tim.com/?ref=lbdpr-docs-tutorial-page"
                target="_blank"
              >
                creative tim website
              </a>{" "}
              and login into your account
            </li>
            <li>
              Go to{" "}
              <a
                href="https://www.creative-tim.com/downloads?ref=lbdpr-docs-tutorial-page"
                target="_blank"
              >
                downloads
              </a>{" "}
              section on creative tim website (be sure to be logged into your
              account)
            </li>
            <li>
              Press the download button near{" "}
              <b>Light Bootstrap Dashboard Pro React</b> product (this will
              download onto your computer a zip file)
            </li>
            <li>Unzip the downloaded file to a folder in your computer</li>
            <li>Open Terminal</li>
            <li>Go to your file project (where you've unzipped the product)</li>
            <li>
              Run in terminal{" "}
              <SyntaxHighlighter
                language="jsx"
                style={prism}
              >{`npm install`}</SyntaxHighlighter>
            </li>
            <li>
              Then run{" "}
              <SyntaxHighlighter
                language="jsx"
                style={prism}
              >{`npm start`}</SyntaxHighlighter>
            </li>
            <li>
              Or you can simply run{" "}
              <SyntaxHighlighter
                language="jsx"
                style={prism}
              >{`npm run install:clean`}</SyntaxHighlighter>{" "}
              which will install{" "}
              <code className="highlighter-rouge">node_modules</code> and also
              will start your project.
            </li>
            <li>
              If you have an error something containing
              <SyntaxHighlighter
                language="jsx"
                style={prism}
              >{`Module not found`}</SyntaxHighlighter>
              you need to copy our <code>jsconfig.json</code> file to your own
              project, if that does not work, it means you are using a different
              configuration than our product. To solve your issue, you will
              either have to change all of our absolute imports to relative
              ones, or, search a way so that your configuration will accept
              absolute paths.
            </li>
            <li>
              If you have an error containing{" "}
              <SyntaxHighlighter
                language="jsx"
                style={prism}
              >{`props.history of undefined`}</SyntaxHighlighter>{" "}
              (this can happen when you integrate our project with another one)
              then you need to make the changes found{" "}
              <a
                href="https://github.com/creativetimofficial/ct-material-dashboard-pro-react/issues/70"
                target="_blank"
              >
                {" "}
                here
              </a>
            </li>
            <li>
              Navigate to{" "}
              <a href="https://localhost:3000" target="_blank">
                https://localhost:3000
              </a>
            </li>
            <li>
              More information →{" "}
              <a
                href="https://reactjs.org/docs/installation.html"
                target="_blank"
              >
                react
              </a>
            </li>
          </ul>
          <h2 id="content" className="text-center">
            Live Production
          </h2>
          <p>Here are some tutorials to help you deploying your app.</p>
          <ul>
            <li>
              <a
                href="https://blog.heroku.com/deploying-react-with-zero-configuration"
                target="_blank"
              >
                tutorial for deploying on heroku
              </a>
            </li>
            <li>
              <a
                href="https://medium.freecodecamp.org/surge-vs-github-pages-deploying-a-create-react-app-project-c0ecbf317089"
                target="_blank"
              >
                tutorial for deploying on github pages and surge
              </a>
            </li>
            <li>
              <a
                href="https://medium.com/@omgwtfmarc/deploying-create-react-app-to-s3-or-cloudfront-48dae4ce0af"
                target="_blank"
              >
                tutorial for deploying on S3 and CloudFront
              </a>
            </li>
          </ul>
        </div>
        <div className="tim-row">
          <h2 className="text-center">File Structure</h2>
          <legend />
          <p>
            Once you have downloaded the archive and opened it, you will find
            the following structure:
          </p>
          <p>Let's take it one by one:</p>
        </div>
        <CodeMirror value={fileStructure} options={options} />
        <div className="tim-row">
          <h2 className="text-center">Restyled Components</h2>
          <legend />
          <p>
            Here is the list of Bootstrap 3 components that we restyled for the
            Light Bootstrap Dashboard:
          </p>
          <ul>
            <li>Buttons</li>
            <li>Collapse</li>
            <li>Dropdown</li>
            <li>Images</li>
            <li>Inputs</li>
            <li>Menu</li>
            <li>Navigation Menu</li>
            <li>Notifications</li>
            <li>Pagination</li>
            <li>Progress Bars</li>
            <li>Select</li>
            <li>Tables</li>
            <li>Textarea</li>
            <li>Tooltips</li>
            <li>Typography</li>
          </ul>
        </div>
        <div className="tim-row">
          <h2 className="text-center">New Components</h2>
          <legend />
          <p>
            Besides giving the existing Bootstrap elements a new look, we added
            new ones, so that the interface and consistent and homogenous. We
            also imported more plugins to use depending on your needs.
          </p>
          <p>Going through them, we added:</p>
          <ul>
            <li> Cards</li>
            <li> Charts</li>
            <li> Checkboxes</li>
            <li> Footers</li>
            <li> Google Maps</li>
            <li> Vector Maps</li>
            <li> Bootstrap Table</li>
            <li> DataTables</li>
            <li> DateTimePicker</li>
            <li> Maps</li>
            <li> Panels</li>
            <li> Radio Buttons</li>
            <li> Sliders</li>
            <li> Sweet Alert</li>
            <li> Switches</li>
            <li> Sidebar</li>
            <li> Tags</li>
            <li> Wizard</li>
          </ul>
        </div>
      </div>
    );
  }
}

export default TutorialSection;
